import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

/** Header padding ceiling at lg+ in px (matches Figma `spacing-6xl`). */
const HEADER_PT_MAX_PX = 48;
/** Header padding floor in px (matches Figma `spacing-xl`). */
const HEADER_PT_MIN_PX = 16;
/** scrollTop, in px, that maps to fully-shrunk header. */
const HEADER_SHRINK_RANGE_PX = HEADER_PT_MAX_PX - HEADER_PT_MIN_PX;

interface IEntityPaneScrollContext {
  /**
   * Ref to the EntityPane wrapper. EntityPaneBody's scroll handler writes
   * `--entity-header-scroll-progress` (0..1) here on every scroll event;
   * EntityPaneHeader reads it via the `entity-header-scroll-shrink`
   * utility (see packages/web/src/styles/app.css).
   *
   * Default `current: null` lets EntityPaneHeader render standalone
   * (e.g. in Storybook) — the scroll handler bails when ref is null.
   */
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

export const EntityPaneScrollContext = React.createContext<IEntityPaneScrollContext>({
  wrapperRef: { current: null },
});

const useEntityPaneScroll = () => React.useContext(EntityPaneScrollContext);

interface IEntityPaneBodyProps {
  /** Full-width content above the two columns at xl+. */
  primaryElement?: React.ReactNode;
  /** Main column at xl+. */
  leftColumn?: React.ReactNode;
  /** Side column at xl+ (347px). */
  rightColumn?: React.ReactNode;
  /** Below xl, render these in order. When omitted, order is primary, right, left. */
  singleColumnOrderOverride?: React.ReactNode[];
  className?: string;
}

/**
 * Scrollable pane body with optional primary block and two-column layout.
 * Responsive: below xl, either stacks singleColumnOrderOverride in order (if provided) or
 * shows right then left; at xl and up, shows primary then left | right.
 *
 * Owns scroll restoration for the pane: since html/body are `overflow: hidden`
 * (see app.css / ORN-548), this scroller is the canonical scroll container,
 * and the global `<ScrollToTop />` cannot reach it. We reset `scrollTop` here
 * on every location change so intra-pane navigation (e.g. switching account
 * id) lands at the top.
 *
 * Drives the scroll-progress CSS variable on the parent EntityPane wrapper so
 * EntityPaneHeader can shrink its top padding and fade in its drop shadow in
 * lockstep with scroll velocity. Direct DOM mutation keeps React out of the
 * per-frame path.
 */
export const EntityPaneBody = ({
  primaryElement,
  leftColumn,
  rightColumn,
  singleColumnOrderOverride,
  className,
}: IEntityPaneBodyProps) => {
  const isXl = useBreakpoint('xl');
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { wrapperRef } = useEntityPaneScroll();
  // Mirrors --entity-header-scroll-progress so we can recover the "natural"
  // scrollable extent (i.e. what scrollHeight−clientHeight would be at
  // progress=0) from inside the scroll handler. Reading the CSS var via
  // getComputedStyle on every scroll event would be expensive.
  const progressRef = React.useRef(0);

  React.useEffect(() => {
    if (scrollRef.current != null) {
      scrollRef.current.scrollTop = 0;
    }
    // Reset the CSS scroll-progress var directly too — setting scrollTop on a
    // scroller that's already at 0 doesn't fire a `scroll` event, so the
    // handler wouldn't otherwise refresh the variable on intra-pane navigation
    // away from a scrolled state.
    if (wrapperRef.current != null) {
      wrapperRef.current.style.setProperty('--entity-header-scroll-progress', '0');
    }
    progressRef.current = 0;
  }, [location.key, wrapperRef]);

  const handleScroll = React.useCallback(() => {
    const scroller = scrollRef.current;
    const wrapper = wrapperRef.current;
    if (scroller == null || wrapper == null) {
      return;
    }
    // Disable the shrink when the body's natural scroll runway can't absorb
    // a full HEADER_SHRINK_RANGE_PX shrink without scrollTop snapping back.
    //
    // The shrink animation grows clientHeight by HEADER_SHRINK_RANGE_PX as
    // progress goes 0→1, which reduces max scrollTop by the same amount.
    // If the user can scroll past HEADER_SHRINK_RANGE_PX, the shrink fires;
    // but if total scrollable runway is less than 2×, the resulting
    // smaller max scrollTop snaps the position back, the handler fires
    // again with reduced progress, the header expands, scrollable grows,
    // scrollTop can advance again, and the loop repeats.
    //
    // Recover the natural scrollable (the value at progress=0) by adding
    // back the shrink contribution to clientHeight (= progressRef.current
    // × HEADER_SHRINK_RANGE_PX) and bail out if it can't accommodate 2×.
    const currentScrollable = scroller.scrollHeight - scroller.clientHeight;
    const naturalScrollable = currentScrollable + progressRef.current * HEADER_SHRINK_RANGE_PX;
    if (naturalScrollable < 2 * HEADER_SHRINK_RANGE_PX) {
      wrapper.style.setProperty('--entity-header-scroll-progress', '0');
      progressRef.current = 0;
      return;
    }
    const progress = Math.min(1, Math.max(0, scroller.scrollTop / HEADER_SHRINK_RANGE_PX));
    wrapper.style.setProperty('--entity-header-scroll-progress', String(progress));
    progressRef.current = progress;
  }, [wrapperRef]);

  const scrollClassName = cx(
    // Padding scales with viewport per Figma: 16px (<sm) → 20px (sm, md, lg) → 32px (xl+).
    `flex h-full w-full flex-col gap-5 overflow-y-auto overflow-x-hidden px-4 pb-4 sm:px-5 sm:pb-5 xl:px-8 xl:pb-8`,
    className
  );
  const hasSingleColumnOrderOverride = singleColumnOrderOverride != null && singleColumnOrderOverride.length > 0;
  const hasColumns = leftColumn != null || rightColumn != null;

  // Mobile: single-column stacked in override order (only one tree mounted)
  if (hasSingleColumnOrderOverride && !isXl) {
    return (
      <div ref={scrollRef} onScroll={handleScroll} className={scrollClassName}>
        {singleColumnOrderOverride.map((section, index) => (
          <div key={index} className="flex min-w-0 flex-col gap-5">
            {section}
          </div>
        ))}
      </div>
    );
  }

  // Desktop (or no override): primary element + two-column layout
  return (
    <div ref={scrollRef} onScroll={handleScroll} className={scrollClassName}>
      {primaryElement != null && <div className="flex flex-col gap-5">{primaryElement}</div>}
      {hasColumns && (
        <div className="flex min-w-0 flex-1 flex-col gap-5 xl:flex-row">
          {/* When no singleColumnOrderOverride, order-1/order-2 make stacked order below xl be primary → right → left (DOM is primary → left → right). */}
          {leftColumn != null && (
            <div
              className={cx(
                'flex min-w-0 flex-1 flex-col gap-5',
                !hasSingleColumnOrderOverride && 'order-2 xl:order-none'
              )}
            >
              {leftColumn}
            </div>
          )}
          {rightColumn != null && (
            <div
              className={cx(
                'flex w-full flex-col gap-5 xl:w-[347px] xl:shrink-0',
                !hasSingleColumnOrderOverride && 'order-1 xl:order-none'
              )}
            >
              {rightColumn}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface IEntityPaneProps {
  children: React.ReactNode;
  className?: string;
}

const EntityPane = ({ children, className }: IEntityPaneProps) => {
  // wrapperRef is the host for `--entity-header-scroll-progress`. The variable
  // cascades to all descendants, so EntityPaneHeader's utility class consumes
  // it without needing its own context subscription.
  // Nesting EntityPanes is not a supported pattern; an inner header would read
  // the outer pane's progress, which is the only sensible default.
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const value = React.useMemo(() => ({ wrapperRef }), []);
  return (
    <EntityPaneScrollContext.Provider value={value}>
      <div ref={wrapperRef} className={cx(`flex h-full w-full flex-col`, className)}>
        {children}
      </div>
    </EntityPaneScrollContext.Provider>
  );
};

export default EntityPane;
